/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box, Typography } from '@mui/material';
import { Call, CallStatus } from '../typings';
import { getCallIConsAndStyles } from '../utils/get-call-icons-and-styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CallDurationTimer } from './CallDurationTimer';
import { ChipRoot, IconWrap, PulseRing } from '../styles';

const ChipView = ({
  onSelect,
  call,
  isAnyCallActive,
  callIndex = 0,
}: {
  onSelect: (call: Call) => void;
  call: Call & { callStartTime?: number };
  isAnyCallActive: boolean;
  callIndex?: number;
}) => {
  const { bg, iconColor, icon, wabaLabelColor } = getCallIConsAndStyles(call.status, 20);

  return (
    <ChipRoot
      onClick={(e) => {
        e.stopPropagation();
        onSelect(call);
      }}
      css={css`
        margin-right: ${callIndex === 1 && isAnyCallActive ? '310px' : '0px'};
        border: ${call.status === CallStatus.IN_PROGRESS ? '1px solid #3D906D' : '1px solid #DADAE4'};
        background-color: ${call.status === CallStatus.END ? 'transparent' : bg};
        gap: 4px;
      `}
      sx={call.status === CallStatus.END ? {
            color: '#7a7a7a',
            borderColor: '#E0E1E4',
            backgroundColor: 'transparent',
            overflow: 'hidden',
            ':hover': {
              backgroundColor: 'transparent',
              borderColor: '#E0E1E4',
            },
            ':before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '50%',
              height: '100%',
              backgroundColor: '#D9D9D9',
              animation: 'fillIn 3s linear forwards',
              zIndex: -1,
            },
            '@keyframes fillIn': {
              from: {
                width: '0%',
              },
              to: {
                width: '100%',
              },
            },
      } : {}}
    >
      <Box position="relative" marginRight={'4px'}>
        <IconWrap
          aria-hidden="true"
          sx={{
            color: iconColor,
          }}
        >
          {icon}
        </IconWrap>
        {call.status === CallStatus.INITIATED && (
          <>
            <PulseRing delay={0} />
            <PulseRing delay={250} />
            <PulseRing delay={500} />
          </>
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: call.status === CallStatus.MISSED ? '#EF5350' : wabaLabelColor, lineHeight: 1, fontSize: 14, whiteSpace: 'nowrap' }}
      >
        {call.customerName || 'Unknown'}
      </Typography>

      {call.status === CallStatus.IN_PROGRESS && call.callStartTime && (
        <CallDurationTimer startTime={call.callStartTime} status={call.status} />
      )}
      {call.status === CallStatus.IN_PROGRESS && <KeyboardArrowDownIcon sx={{ color: wabaLabelColor }} />}
    </ChipRoot>
  );
};

export default ChipView;